import React, { useState, useEffect, useRef } from 'react';
import { getForecastStock } from '../api';
import { SUMMARY } from './ForecastStocks';

/**
 * Вкладка «Акція» сторінки «Прогнози»: один папір на момент «дата + година».
 *
 * Графік (свічки + SMA 20/50/200 + смуга «Резюме» під ним + точки розвороту
 * Classic справа), 12 індикаторів і 12 середніх з мітками, точки розвороту
 * п'ятьма методиками, зміни «Резюме», консенсус аналітиків і його історія.
 *
 * Точки розвороту — зі свічки ПЕРЕД поточною: так рахує investing.com
 * (звірено з 21 знімком, медіана розходження 0.004–0.009% ціни).
 */

const STATE_ID = { strong_buy: 2, buy: 1, neutral: 0, sell: -1, strong_sell: -2 };
const LABEL_UA = {
  Buy: { text: 'Купувати', color: '#007A41', bg: '#E7F7EF' },
  Sell: { text: 'Продавати', color: '#C02B5E', bg: '#FDE8EF' },
  Neutral: { text: 'Нейтрально', color: '#5B646F', bg: '#F1F3F5' },
  Overbought: { text: 'Перекупленість', color: '#92400E', bg: '#FEF3C7' },
  Oversold: { text: 'Перепроданість', color: '#92400E', bg: '#FEF3C7' },
  Volatility: { text: 'Волатильність', color: '#5B646F', bg: '#F1F3F5' },
};
// Категорійні кольори (перевірено валідатором палітри): не зелений/червоний,
// бо ними вже позначені свічки.
const MA_COLORS = { 20: '#2a78d6', 50: '#eb6834', 200: '#4a3aa7' };
const HOURS = [10, 11, 12, 13, 14, 15, 16];
const PIVOT_ROWS = ['r3', 'r2', 'r1', 'pivot', 's1', 's2', 's3'];
const PIVOT_METHODS = [['classic', 'Classic'], ['fibonacci', 'Fibonacci'], ['camarilla', 'Camarilla'],
                       ['woodie', 'Woodie'], ['demark', 'DeMark']];

const fmtP = (v) => (v === null || v === undefined ? '—' : v < 1 ? v.toFixed(4) : v.toFixed(2));
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`);
function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)} трлн`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)} млрд`;
  return `$${(v / 1e6).toFixed(1)} млн`;
}

// «з 61 аналітика», «з 59 аналітиків»: після «з» — родовий; на 1 (крім 11) — однина.
const analystsOf = (n) => `з ${n} ${n % 10 === 1 && n % 100 !== 11 ? 'аналітика' : 'аналітиків'}`;

function Badge({ state, big }) {
  const st = SUMMARY[STATE_ID[state] ?? state];
  if (!st) return <span>—</span>;
  return (
    <span style={{ ...S.badge, background: st.bg, color: st.color,
                   ...(big ? { fontSize: 15, padding: '4px 12px' } : {}) }}>{st.label}</span>
  );
}

function LabelBadge({ label }) {
  const l = LABEL_UA[label] || { text: label, color: '#5B646F', bg: '#F1F3F5' };
  return <span style={{ ...S.badge, background: l.bg, color: l.color }}>{l.text}</span>;
}

/** Свічковий графік. Одна вісь ціни; смуга станів «Резюме» — окремою доріжкою знизу. */
function Chart({ chart, pivots, timeframe }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(null);
  const W = 1000, PH = 300, SH = 14, GAP = 8, PADR = 64, PADL = 8;
  const n = chart.t.length;
  const lows = chart.l.filter((v) => v !== null);
  const highs = chart.h.filter((v) => v !== null);
  const cl = pivots?.classic || {};
  const piv = ['s1', 'pivot', 'r1'].map((k) => cl[k]).filter((v) => v !== null && v !== undefined);
  let lo = Math.min(...lows, ...piv), hi = Math.max(...highs, ...piv);
  const pad = (hi - lo) * 0.04 || 1;
  lo -= pad; hi += pad;
  const plotW = W - PADL - PADR;
  const step = plotW / n;
  const x = (i) => PADL + step * (i + 0.5);
  const y = (v) => 6 + (PH - 12) * (1 - (v - lo) / (hi - lo));
  const bw = Math.max(1, step * 0.6);

  // Годинні S1/P/R1 часто ближчі за висоту рядка — підписи розводимо вертикально
  // (лінії лишаються на своїх рівнях).
  const pivLabels = [['r1', 'R1'], ['pivot', 'P'], ['s1', 'S1']]
    .filter(([k]) => cl[k] !== null && cl[k] !== undefined)
    .map(([k, lab]) => ({ k, lab, v: cl[k], ly: y(cl[k]) }));
  pivLabels.forEach((p, i) => {
    if (i > 0 && p.ly < pivLabels[i - 1].ly + 12) p.ly = pivLabels[i - 1].ly + 12;
  });
  const ticks = Array.from({ length: 5 }, (_, i) => lo + ((hi - lo) * (i + 0.5)) / 5);
  const line = (arr) => {
    let d = '';
    arr.forEach((v, i) => {
      if (v === null) return;
      d += `${d && arr[i - 1] !== null ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
    });
    return d;
  };

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const i = Math.floor((px - PADL) / step);
    setHover(i >= 0 && i < n ? i : null);
  };

  const h = hover;
  return (
    <div style={{ position: 'relative' }}>
      <div style={S.legend}>
        {Object.entries(MA_COLORS).map(([m, col]) => (
          <span key={m} style={S.legendItem}>
            <span style={{ ...S.swatch, background: col }} />SMA {m}
          </span>
        ))}
        <span style={S.legendItem}><span style={{ ...S.swatch, background: '#8A929C', height: 1 }} />
          Точки розвороту Classic (S1 · P · R1)</span>
        <span style={{ ...S.legendItem, color: '#8A929C' }}>
          смуга знизу — «Резюме» {timeframe} на кожній свічці
        </span>
      </div>
      <svg ref={ref} viewBox={`0 0 ${W} ${PH + GAP + SH + 18}`} style={{ width: '100%', display: 'block' }}
           onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PADL} x2={W - PADR} y1={y(v)} y2={y(v)} stroke="#F1F3F5" />
            <text x={W - PADR + 6} y={y(v) + 4} fontSize="11" fill="#8A929C">{fmtP(v)}</text>
          </g>
        ))}
        {chart.t.map((_, i) => {
          const o = chart.o[i], c = chart.c[i];
          const up = c >= o;
          const col = up ? '#00A656' : '#C02B5E';
          const top = y(Math.max(o, c)), bot = y(Math.min(o, c));
          return (
            <g key={i}>
              <line x1={x(i)} x2={x(i)} y1={y(chart.h[i])} y2={y(chart.l[i])} stroke={col} strokeWidth="1" />
              <rect x={x(i) - bw / 2} y={top} width={bw} height={Math.max(1, bot - top)} fill={col} />
            </g>
          );
        })}
        {Object.entries(MA_COLORS).map(([m, col]) => (
          <path key={m} d={line(chart[`sma${m}`])} fill="none" stroke={col} strokeWidth="2" />
        ))}
        {/* Точки розвороту — поверх свічок і середніх, підпис з білим ореолом. */}
        {pivLabels.map(({ k, lab, v, ly }) => (
          <g key={k}>
            <line x1={PADL + plotW * 0.75} x2={W - PADR} y1={y(v)} y2={y(v)}
                  stroke="#8A929C" strokeDasharray="4 3" />
            <text x={PADL + plotW * 0.75 - 4} y={ly + 4} fontSize="10" fill="#5B646F"
                  textAnchor="end" stroke="#fff" strokeWidth="3" paintOrder="stroke">{lab} {fmtP(v)}</text>
          </g>
        ))}
        {chart.s.map((s, i) => {
          const st = s === null ? null : SUMMARY[s];
          return (
            <rect key={i} x={x(i) - step / 2} y={PH + GAP} width={step + 0.2} height={SH}
                  fill={st ? st.color : '#E5E7EB'} opacity={st ? 0.85 : 1} />
          );
        })}
        {[0, Math.floor(n / 2), n - 1].map((i) => (
          <text key={i} x={x(i)} y={PH + GAP + SH + 14} fontSize="11" fill="#8A929C"
                textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>{chart.t[i]}</text>
        ))}
        {h !== null && (
          <line x1={x(h)} x2={x(h)} y1={0} y2={PH + GAP + SH} stroke="#1A1A1A" strokeOpacity="0.25" />
        )}
        <rect x={0} y={0} width={W} height={PH + GAP + SH} fill="transparent" />
      </svg>
      {h !== null && (
        <div style={{ ...S.tip, left: `${Math.min(80, (x(h) / W) * 100)}%` }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{chart.t[h]}</div>
          <div>Відкр. {fmtP(chart.o[h])} · Макс. {fmtP(chart.h[h])}</div>
          <div>Мін. {fmtP(chart.l[h])} · Закр. <b>{fmtP(chart.c[h])}</b></div>
          {Object.keys(MA_COLORS).map((m) => (
            <div key={m}>SMA {m}: {fmtP(chart[`sma${m}`][h])}</div>
          ))}
          <div style={{ marginTop: 4 }}>{chart.s[h] === null ? '—' : <Badge state={chart.s[h]} />}</div>
        </div>
      )}
    </div>
  );
}

export default function ForecastStock({ ticker: initialTicker, at, onTicker }) {
  const [ticker, setTicker] = useState(initialTicker || 'AAPL');
  const [input, setInput] = useState(initialTicker || 'AAPL');
  const [timeframe, setTimeframe] = useState('1h');
  const [date, setDate] = useState(at?.date || '');
  const [hour, setHour] = useState(at?.hour || 16);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Відкрито з таблиці «Акції» — беремо її тікер, дату й годину.
  useEffect(() => {
    if (initialTicker) { setTicker(initialTicker); setInput(initialTicker); }
    if (at) { setDate(at.date || ''); setHour(at.hour || 16); }
  }, [initialTicker, at]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getForecastStock(ticker, timeframe, date || null, hour)
      .then((res) => { if (alive) setData(res.data); })
      .catch((e) => { if (alive) { setData(null); setError(e?.response?.data?.detail || String(e)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ticker, timeframe, date, hour]);

  const go = () => {
    const t = input.trim().toUpperCase();
    if (t) { setTicker(t); if (onTicker) onTicker(t); }
  };
  const days = data?.days || [];
  const cons = data?.consensus;
  const changed = input.trim().toUpperCase() !== '' && input.trim().toUpperCase() !== ticker;

  return (
    <div>
      <div style={S.controls}>
        <label style={S.field}>
          <span style={S.fieldLabel}>Тікер</span>
          <span style={{ display: 'flex', gap: 4 }}>
            <input style={{ ...S.input, width: 110 }} value={input} placeholder="тікер, Enter"
                   onChange={(e) => setInput(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && go()} />
            {/* Активна лише коли в полі ІНШИЙ тікер, ніж на екрані: інакше натискання
                нічого не змінює, і кнопка виглядала зламаною. */}
            <button style={{ ...S.tab, ...(changed ? S.tabActive : { opacity: 0.5, cursor: 'default' }) }}
                    disabled={!changed} onClick={go}>Відкрити</button>
          </span>
        </label>
        <label style={S.field}>
          <span style={S.fieldLabel}>Таймфрейм</span>
          <span style={{ display: 'flex', gap: 4 }}>
            {[['1h', 'Година'], ['1d', 'День']].map(([id, l]) => (
              <button key={id} onClick={() => setTimeframe(id)}
                      style={{ ...S.tab, ...(timeframe === id ? S.tabActive : {}) }}>{l}</button>
            ))}
          </span>
        </label>
        <label style={S.field}>
          <span style={S.fieldLabel}>Дата</span>
          <select style={S.input} value={date} onChange={(e) => setDate(e.target.value)}>
            <option value="">останній бар</option>
            {[...days].reverse().map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label style={S.field}>
          <span style={S.fieldLabel}>Година (Нью-Йорк)</span>
          <select style={S.input} value={hour} disabled={!date} onChange={(e) => setHour(Number(e.target.value))}>
            {HOURS.map((h) => <option key={h} value={h}>{h}:00{h === 16 ? ' (закриття)' : ''}</option>)}
          </select>
        </label>
      </div>

      {error && <div style={S.err}>{error}</div>}
      {loading && !data && <div style={S.info}>Завантаження…</div>}

      {data && (
        <div style={{ opacity: loading ? 0.5 : 1 }}>
          <div style={S.head}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>{data.ticker}
                <span style={{ fontWeight: 400, color: '#5B646F', fontSize: 14, marginLeft: 10 }}>{data.name || ''}</span>
              </div>
              <div style={S.meta}>
                ${fmtP(data.price)} · станом на {timeframe === '1h' ? `${data.at_close} (бар ${data.at.slice(11)}–${data.at_close.slice(11)})` : `закриття ${data.at.slice(0, 10)}`}
                {timeframe === '1d' && date && hour < 16 && ' — до 16:00 денна свічка ще не закрита, тому стан попереднього дня'}
              </div>
            </div>
            <div style={S.sumBox}>
              <div style={S.fieldLabel}>Резюме {timeframe}</div>
              <Badge state={data.summary} big />
              <div style={S.small}>
                Індикатори: <Badge state={data.ind_block} /> {data.ind_buy}↑ {data.ind_sell}↓ з 11 ·
                {' '}Середні: <Badge state={data.ma_block} /> {data.ma_buy}/12 нижче ціни
              </div>
            </div>
          </div>

          <div style={S.card}><Chart chart={data.chart} pivots={data.pivots} timeframe={timeframe} /></div>

          <div style={S.grid}>
            <div style={S.card}>
              <h3 style={S.h3}>Технічні індикатори</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead><tr><th style={{ ...S.th, textAlign: 'left' }}>Індикатор</th>
                    <th style={S.th}>Значення</th><th style={{ ...S.th, textAlign: 'left' }}>Сигнал</th></tr></thead>
                  <tbody>
                    {data.indicators.map((r) => (
                      <tr key={r.key} style={S.tr}>
                        <td style={S.td}>{r.name}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{r.value === null ? '—' : r.value.toFixed(3)}</td>
                        <td style={S.td}><LabelBadge label={r.label} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={S.card}>
              <h3 style={S.h3}>Ковзні середні</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead><tr><th style={{ ...S.th, textAlign: 'left' }}>Період</th>
                    <th style={S.th}>SMA</th><th style={{ ...S.th, textAlign: 'left' }} />
                    <th style={S.th}>EMA</th><th style={{ ...S.th, textAlign: 'left' }} /></tr></thead>
                  <tbody>
                    {data.mas.map((r) => (
                      <tr key={r.period} style={S.tr}>
                        <td style={S.td}>MA{r.period}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{fmtP(r.sma)}</td>
                        <td style={S.td}><LabelBadge label={r.sma_label} /></td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{fmtP(r.ema)}</td>
                        <td style={S.td}><LabelBadge label={r.ema_label} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={S.card}>
            <h3 style={S.h3}>Точки розвороту</h3>
            <div style={S.meta}>Зі свічки {data.pivot_from}{timeframe === '1h' ? ' (попередній годинний бар)' : ' (попередній день)'}</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead><tr><th style={{ ...S.th, textAlign: 'left' }}>Рівень</th>
                  {PIVOT_METHODS.map(([k, l]) => <th key={k} style={S.th}>{l}</th>)}</tr></thead>
                <tbody>
                  {PIVOT_ROWS.map((row) => (
                    <tr key={row} style={{ ...S.tr, ...(row === 'pivot' ? { background: '#F9FAFB', fontWeight: 600 } : {}) }}>
                      <td style={S.td}>{row === 'pivot' ? 'Точка розвороту' : row.toUpperCase()}</td>
                      {PIVOT_METHODS.map(([k]) => (
                        <td key={k} style={{ ...S.td, textAlign: 'right' }}>{fmtP(data.pivots[k]?.[row])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={S.grid}>
            <div style={S.card}>
              <h3 style={S.h3}>Зміни «Резюме» на графіку</h3>
              {data.changes.length === 0 && <div style={S.meta}>Стан не змінювався.</div>}
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <tbody>
                    {[...data.changes].reverse().map((r) => (
                      <tr key={r.t} style={S.tr}>
                        <td style={S.td}>{r.t}</td>
                        <td style={S.td}><Badge state={r.from} /> → <Badge state={r.to} /></td>
                        <td style={{ ...S.td, textAlign: 'right' }}>${fmtP(r.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={S.card}>
              <h3 style={S.h3}>Консенсус аналітиків (Yahoo)</h3>
              {!cons && <div style={S.meta}>Немає даних консенсусу для цього паперу.</div>}
              {cons && (<>
                <div style={S.consRow}>
                  <div><div style={S.fieldLabel}>Ціль</div><b>${fmtP(cons.target)}</b></div>
                  <div><div style={S.fieldLabel} title="Середня ціль аналітиків відносно поточної ціни (горизонт ~12 міс.)">Прогноз росту</div>
                    <b style={{ color: cons.upside > 0 ? '#007A41' : '#C02B5E' }}>{fmtPct(cons.upside)}</b></div>
                  {/* Два різні поля Yahoo: рейтинги мають усі аналітики, що ведуть акцію,
                      а цільову ціну публікує лише частина з них — тому цілей буває менше. */}
                  <div><div style={S.fieldLabel} title="Скільки аналітиків опублікували цільову ціну — з них рахується середня ціль">Цільових цін</div>
                    <b>{cons.n ?? '—'}</b></div>
                  <div><div style={S.fieldLabel}>Купити / тримати / продати</div>
                    <b><span style={{ color: '#007A41' }}>{cons.buy}</span> / {cons.hold} /
                      {' '}<span style={{ color: '#C02B5E' }}>{cons.sell}</span></b>
                    <span style={S.small}> ({analystsOf((cons.buy || 0) + (cons.hold || 0) + (cons.sell || 0))})</span></div>
                  <div><div style={S.fieldLabel}>Капіталізація</div><b>{fmtMoney(cons.cap)}</b></div>
                </div>
                <div style={S.meta}>Знімок {cons.date}. Консенсус збирається щодня з 06.10.2026, тож історія поки коротка
                  {data.at.slice(0, 10) < cons.date ? ' і пізніша за вибрану дату' : ''}.</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={S.table}>
                    <thead><tr>{['Дата', 'Ціль', 'Прогноз росту', 'Цільових цін', 'К / Т / П'].map((l) => (
                      <th key={l} style={{ ...S.th, textAlign: l === 'Дата' ? 'left' : 'right' }}>{l}</th>))}</tr></thead>
                    <tbody>
                      {[...data.consensus_history].reverse().map((r) => (
                        <tr key={r.date} style={S.tr}>
                          <td style={S.td}>{r.date}</td>
                          <td style={{ ...S.td, textAlign: 'right' }}>${fmtP(r.target)}</td>
                          <td style={{ ...S.td, textAlign: 'right' }}>{fmtPct(r.upside)}</td>
                          <td style={{ ...S.td, textAlign: 'right' }}>{r.n ?? '—'}</td>
                          <td style={{ ...S.td, textAlign: 'right' }}>{r.buy} / {r.hold} / {r.sell}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  controls: { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldLabel: { fontSize: 12, color: '#5B646F' },
  input: { padding: '6px 8px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 13,
           color: '#1A1A1A', background: '#fff' },
  tab: { padding: '6px 12px', borderRadius: 6, border: '1px solid #E5E7EB',
         background: '#FFFFFF', color: '#5B646F', cursor: 'pointer', fontSize: 13 },
  tabActive: { background: '#475FFA', border: '1px solid #475FFA', color: '#fff' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16,
          flexWrap: 'wrap', marginBottom: 12 },
  sumBox: { display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' },
  small: { fontSize: 12, color: '#5B646F' },
  meta: { margin: '4px 0 8px', fontSize: 12, color: '#5B646F', lineHeight: 1.5 },
  card: { border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', padding: 14, marginBottom: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 14 },
  h3: { margin: '0 0 8px', fontSize: 15, color: '#1A1A1A' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '6px 8px', color: '#5B646F', textAlign: 'right', whiteSpace: 'nowrap',
        borderBottom: '1px solid #E5E7EB', fontWeight: 600 },
  tr: { borderBottom: '1px solid #F1F3F5' },
  td: { padding: '5px 8px', color: '#1A1A1A', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  badge: { padding: '2px 8px', borderRadius: 4, fontSize: 12, whiteSpace: 'nowrap', fontWeight: 500 },
  legend: { display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: '#5B646F', marginBottom: 6 },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 6 },
  swatch: { display: 'inline-block', width: 14, height: 2, borderRadius: 1 },
  tip: { position: 'absolute', top: 30, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6,
         padding: '8px 10px', fontSize: 12, color: '#1A1A1A', pointerEvents: 'none',
         boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  consRow: { display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 8, fontSize: 14 },
  info: { padding: 16, color: '#5B646F' },
  err: { padding: 12, background: '#FDE8EF', border: '1px solid #F5C2D3', borderRadius: 6,
         color: '#C02B5E', marginBottom: 12 },
};
